import { makeStyles } from '@mui/styles';
import Badge from 'apollo-react/components/Badge';
import CircularProgress from 'apollo-react/components/CircularProgress';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip/Tooltip';
import Filter from 'apollo-react-icons/Filter';
import Sync from 'apollo-react-icons/Sync';
import Undo from 'apollo-react-icons/Undo';
import React, { useState } from 'react';

import AdvancedSearchIcon from './AdvancedSearchIcon';
import AdvancedSearchModal from './AdvancedSearchModal';
import ExportIcon from './ExportIcon';
import { useFormSelector } from '../../custom-hooks';
import { getSavedSearchResultsRows } from '../../form-designer/selectors/runtime.selectors';
import { getSavedSearchResultsKey } from '../../reducers/SavedSearchResultsReducer';
import { getUITA } from '../../utils/commonUtils';
import withTranslation from '../withTranslation';

const useStyles = makeStyles({
  buttonsContainer: {
    display: 'flex',
  },
  filterButton: {
    paddingTop: '2px',
  },
  customIconButton: {
    width: '1.3rem',
  },
  loaderContainer: {
    width: 32,
    height: 32,
  },
  clearResultButton: {
    paddingTop: '4px',
    width: '22px',
    height: '22px',
  },
  exportPending: {
    margin: 6,
  },
  customIconHover: {
    '&:hover use': {
      fill: '#000',
    },

    '&:hover g': {
      fill: '#000',
    },
  },
  badgeDot: {
    '& .MuiBadge-dot': {
      height: 8,
      minWidth: 8,
      borderRadius: '50%',
      top: '-2px',
      right: '-2px',
    },
  },
});

function CustomHeader({
  toggleFilters,
  handleExport,
  entityName,
  searchName = '',
  dataKey = '',
  onSearch = () => {},
  hideAdvancedSearch = false,
  hideFilters = false,
  hideExport = false,
  CustomActions = () => null,
  initialCriteria,
  customAdvancedSearchCriteria,
  translateString,
  onRefreshTableData,
  handleClearTableResults,
  savedSearchFilters = {},
  isSavedSearchExportPending,
  clearFilters: clearInternalFilters,
}) {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const savedSearchKey = getSavedSearchResultsKey(entityName, searchName, dataKey);
  const rows = useFormSelector(getSavedSearchResultsRows, { key: savedSearchKey });

  const shouldDisplayClearFiltersButton =
    savedSearchFilters.filters?.length ||
    savedSearchFilters.sortedColumn ||
    savedSearchFilters.sortOrder ||
    savedSearchFilters.advancedSearchCriteria ||
    savedSearchFilters.tablePage > 0;

  return (
    <div className={classes.buttonsContainer}>
      {shouldDisplayClearFiltersButton && (
        <Tooltip title={translateString('Clear active filters')} placement="top">
          <Badge className={classes.badgeDot} backgroundColor="decorative2" variant="dot">
            <IconButton
              aria-label={translateString('Clear active filters')}
              style={{ marginRight: 5 }}
              size="small"
              onClick={() => {
                handleClearTableResults();
                clearInternalFilters();
              }}
              ui-ta={getUITA('clear-filters')}
            >
              <Undo fontSize="small" className={classes.clearResultButton} />
            </IconButton>
          </Badge>
        </Tooltip>
      )}
      {!hideFilters && (
        <Tooltip title={translateString('Filter')} placement="top">
          <IconButton
            aria-label={translateString('Filter')}
            style={{ marginRight: 5 }}
            onClick={toggleFilters}
            size="small"
            ui-ta={getUITA('table-filter')}
          >
            <Filter fontSize="small" className={classes.filterButton} />
          </IconButton>
        </Tooltip>
      )}
      {!hideAdvancedSearch && (
        <Tooltip title={translateString('Advanced Search')} placement="top">
          <IconButton
            aria-label={translateString('Advanced Search')}
            style={{ marginRight: 5 }}
            size="small"
            className={classes.customIconHover}
            onClick={() => {
              setOpen(true);
            }}
            ui-ta={getUITA('table-adv-search')}
          >
            <AdvancedSearchIcon className={classes.customIconButton} />
          </IconButton>
        </Tooltip>
      )}
      {!hideExport && !isSavedSearchExportPending && rows?.length > 0 && (
        <Tooltip title={translateString('Export')} placement="top">
          <IconButton
            aria-label={translateString('Export')}
            size="small"
            onClick={handleExport}
            className={classes.customIconHover}
            ui-ta={getUITA('table-export')}
          >
            <ExportIcon className={classes.customIconButton} />
          </IconButton>
        </Tooltip>
      )}
      {isSavedSearchExportPending && (
        <Tooltip title={translateString('Export Pending')} placement="top">
          <div className={classes.loaderContainer}>
            <CircularProgress
              className={classes.exportPending}
              color="inherit"
              variant="indeterminate"
              size={20}
            />
          </div>
        </Tooltip>
      )}
      <Tooltip title={translateString('Refresh')} placement="top">
        <IconButton
          aria-label={translateString('Refresh')}
          style={{ marginRight: 5 }}
          size="small"
          onClick={onRefreshTableData}
          ui-ta={getUITA('table-refresh')}
        >
          <Sync fontSize="small" />
        </IconButton>
      </Tooltip>
      {<CustomActions />}
      <AdvancedSearchModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        entityName={entityName}
        searchName={searchName}
        dataKey={dataKey}
        onSearch={onSearch}
        initialCriteria={initialCriteria}
        customAdvancedSearchCriteria={customAdvancedSearchCriteria}
      />
    </div>
  );
}

export default withTranslation(CustomHeader);
